import { useNavigate } from 'react-router-dom';
import { useClientTranslation } from '@/lib/core/i18n';
import type { Image } from '@/lib/core/utils/image';
import type { Link } from '@/lib/core/utils/links';

export interface ShopDiscoverBannerProps {
  image?: Image;
  title?: string;
  description?: string;
  link?: Link;
}

export function ShopDiscoverBanner({ image, title, description, link }: ShopDiscoverBannerProps) {
  const t = useClientTranslation();
  const navigate = useNavigate();

  /*
   * group-last: only when this banner is the *last* module on the page (its wrapping `<section>`
   * carries `group` and is `:last-child` — see byCategoryCategoryContent/byCategoryBrandContent/
   * byCategoryCollectionContent) does the title/description need to clear the floating AppNavBar
   * pill (fixed at `bottom: 1.5rem + safe-area-inset-bottom`, `h-12` tall — see
   * components/layout/navBar/index.tsx) plus a small gap. Every other instance keeps the design's
   * plain `pb-10`, sitting close to the image's bottom edge. Fixed on the text itself, not as
   * extra page-level bottom padding, so the image keeps filling the viewport instead of leaving a
   * blank gap below it.
   */
  const content = (
    <div className="relative flex aspect-402/624 w-full flex-col items-center justify-end gap-8 px-4 pb-10 group-last:pb-[calc(5.5rem+env(safe-area-inset-bottom))]">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="bg-jncquoi-neutral-1000 absolute inset-0 overflow-hidden">
          {image?.src && (
            <img
              alt={image.alt ?? ''}
              className="absolute inset-0 h-full w-full object-cover"
              src={image.src}
            />
          )}
        </div>
        <div className="absolute inset-0 bg-linear-to-b from-[rgba(0,0,0,0.12)] from-0% to-[rgba(0,0,0,0.4)] to-79%" />
      </div>
      <div className="relative flex w-full flex-col gap-6 text-white">
        {description && (
          <p className="font-pp-neue-montreal text-[12px] leading-none font-normal tracking-[0.5px] uppercase">
            {description ?? t('App.Shop.Discover')}
          </p>
        )}
        {title && (
          <p className="font-pp-neue-montreal text-[28px] leading-none font-semibold tracking-[0.5px] uppercase">
            {title}
          </p>
        )}
      </div>
    </div>
  );

  if (link?.href) {
    return (
      <button
        type="button"
        className="w-full cursor-pointer text-left"
        onClick={() => (link.newTab ? window.open(link.href, '_blank') : navigate(link.href))}
      >
        {content}
      </button>
    );
  }

  return content;
}
